import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const getAuthToken = () => {
  let storedUser = {};

  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("userToken") ||
    localStorage.getItem("adminToken") ||
    storedUser?.token ||
    storedUser?.accessToken ||
    ""
  );
};

const getAuthConfig = () => {
  const token = getAuthToken();

  return token
    ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    : {
        headers: {},
      };
};

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.vendors)) return data.vendors;
  if (Array.isArray(data?.payments)) return data.payments;
  if (Array.isArray(data?.pos)) return data.pos;
  if (data && typeof data === "object") return [data];
  return [];
};

const logApiError = (label, err) => {
  console.error(label, {
    url: err?.config?.url,
    method: err?.config?.method,
    status: err?.response?.status,
    data: err?.response?.data,
    message: err?.message,
  });
};

const VendorPO = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const project = location.state?.project || null;

  const [vendors, setVendors] = useState([]);
  const [savedPOs, setSavedPOs] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);

  const canViewVendorPO = canAccess("vendorPO", "view");
  const canOpenVendorPO = canViewVendorPO;

  useEffect(() => {
    fetchVendors();

    if (project?.name) {
      fetchSavedVendorPOs();
      fetchVendorPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.name]);

  const fetchVendorPayments = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/vendor-payments?projectName=${encodeURIComponent(
          project.name,
        )}`,
        getAuthConfig(),
      );

      setVendorPayments(normalizeArray(res.data));
    } catch (err) {
      if (err?.response?.status === 404) {
        setVendorPayments([]);
        return;
      }

      logApiError("Error fetching vendor payments", err);
      setVendorPayments([]);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/vendors`, getAuthConfig());
      setVendors(normalizeArray(res.data));
    } catch (err) {
      logApiError("Error fetching vendors", err);
      setVendors([]);
    }
  };

  const fetchSavedVendorPOs = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/vendor-po?projectName=${encodeURIComponent(
          project.name,
        )}`,
        getAuthConfig(),
      );

      setSavedPOs(normalizeArray(res.data));
    } catch (err) {
      if (err?.response?.status === 404) {
        setSavedPOs([]);
        return;
      }

      logApiError("Error fetching saved PO", err);
      setSavedPOs([]);
    }
  };

  useEffect(() => {
    if (!project?.name) return undefined;
    const refresh = () => void fetchSavedVendorPOs();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.name]);

  const activePOs = savedPOs.filter(
    (po) => Array.isArray(po.items) && po.items.length > 0,
  );

  // Refresh once when returning from detail / another browser tab.
  // Existing empty PO records and their documents remain safely in MongoDB.

  const filteredVendors = vendors.filter((vendor) =>
    activePOs.some((po) => String(po.vendorId) === String(vendor._id)),
  );
  const getVendorPOTotal = (vendorId) => {
    const vendorPOs = activePOs.filter(
      (p) => String(p.vendorId) === String(vendorId),
    );

    return vendorPOs.reduce((sum, po) => sum + Number(po?.totalAmount || 0), 0);
  };

  const getVendorPaidAmount = (vendorId) => {
    return vendorPayments
      .filter((payment) => String(payment.vendorId) === String(vendorId))
      .reduce((sum, payment) => sum + Number(payment.paymentAmt || 0), 0);
  };

  const openVendorPO = (vendor) => {
    if (!canOpenVendorPO) return;

    navigate(`/vendor-po/${vendor._id}`, {
      state: { project, vendor },
    });
  };

  const addNewVendorPO = () => {
    const token = getAuthToken();

    if (!token) {
      alert("Login token not found. Please login again.");
      return;
    }

    navigate("/vendor-po/new", {
      state: {
        project,
        isNewPO: true,
      },
    });
  };

  if (!canViewVendorPO) {
    return (
      <div style={{ padding: 30, color: "red", fontWeight: 700 }}>
        You do not have permission to view Vendor PO.
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button
          onClick={() =>
            navigate("/VendorQuote", {
              state: { project },
            })
          }
          style={styles.backBtn}
        >
          ← Back
        </button>

        <div style={styles.headerCenter}>
          <h2 style={styles.title}>Vendor PO List</h2>
          <p style={styles.projectText}>Project: {project?.name || "-"}</p>
        </div>

        <div style={styles.headerRight}>
          <button onClick={addNewVendorPO} style={styles.addBtn}>
            + Add New Vendor PO
          </button>

          <div style={styles.countBadge}>{filteredVendors.length} Vendors</div>
        </div>
      </div>

      {!project?.name && (
        <div style={styles.errorBox}>
          Project not found. Please open this page from project.
        </div>
      )}

      {project?.name && filteredVendors.length === 0 && (
        <div style={styles.errorBox}>
          No Vendor PO found for this project. Click Add New Vendor PO to
          create.
        </div>
      )}

      <div style={styles.grid}>
        {filteredVendors.map((vendor) => {
          const total = getVendorPOTotal(vendor._id);
          const paid = getVendorPaidAmount(vendor._id);
          const pending = Math.max(total - paid, 0);
          const workDone =
            total > 0 ? ((paid / total) * 100).toFixed(1) : "0.0";

          return (
            <div
              key={vendor._id}
              style={{
                ...styles.card,
                cursor: canOpenVendorPO ? "pointer" : "default",
              }}
              onClick={() => openVendorPO(vendor)}
            >
              <div style={styles.cardTop}>
                <div>
                  <div style={styles.vendorCode}>
                    {vendor.vendorCode || "NA"}
                  </div>
                  <h3 style={styles.vendorName}>{vendor.fullName}</h3>
                </div>

                <div style={styles.totalBox}>
                  ₹{" "}
                  {total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div style={styles.infoRow}>
                <span>Amount Paid</span>
                <b style={{ color: "#16a34a" }}>
                  ₹ {paid.toLocaleString("en-IN")}
                </b>
              </div>

              <div style={styles.infoRow}>
                <span>Pending Amount</span>
                <b style={{ color: "#dc2626" }}>
                  ₹{" "}
                  {pending.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </b>
              </div>

              <div style={styles.infoRow}>
                <span>Work Done</span>
                <b style={{ color: "#2563eb" }}>{workDone}%</b>
              </div>

              {canOpenVendorPO && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openVendorPO(vendor);
                  }}
                  style={styles.openBtn}
                >
                  Open Vendor PO →
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    padding: "34px",
    background: "linear-gradient(135deg, #f5f7fb 0%, #eef4ff 100%)",
    fontFamily: "Calibri, Arial, sans-serif",
  },

  header: {
    background: "#fff",
    borderRadius: "16px",
    padding: "18px 22px",
    marginBottom: "26px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    display: "grid",
    gridTemplateColumns: "120px 1fr 300px",
    alignItems: "center",
    gap: "15px",
    border: "1px solid #e5e7eb",
  },

  backBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "9px",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: "14px",
  },

  headerCenter: {
    textAlign: "center",
  },

  headerRight: {
    justifySelf: "end",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  addBtn: {
    background: "#16a34a",
    color: "#fff",
    border: "none",
    padding: "10px 14px",
    borderRadius: "9px",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: "14px",
    whiteSpace: "nowrap",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: 900,
    color: "#0f172a",
  },

  projectText: {
    margin: "6px 0 0",
    color: "#475569",
    fontWeight: 700,
  },

  countBadge: {
    background: "#ecfdf5",
    color: "#047857",
    border: "1px solid #a7f3d0",
    padding: "9px 15px",
    borderRadius: "999px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  errorBox: {
    padding: "14px",
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    fontWeight: 800,
    marginBottom: "18px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    alignItems: "start",
  },

  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
  },

  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "18px",
  },

  vendorCode: {
    display: "inline-block",
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "4px 10px",
    borderRadius: "999px",
    fontWeight: 900,
    fontSize: "12px",
    marginBottom: "8px",
  },

  vendorName: {
    margin: 0,
    fontSize: "21px",
    fontWeight: 900,
    color: "#0f172a",
  },

  totalBox: {
    background: "#f0fdf4",
    color: "#166534",
    border: "1px solid #bbf7d0",
    padding: "8px 12px",
    borderRadius: "10px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #f1f5f9",
    color: "#475569",
    fontSize: "15px",
  },

  openBtn: {
    marginTop: "18px",
    width: "100%",
    background: "#008080",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "12px 16px",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: "15px",
  },
};

export default VendorPO;
